import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAreas } from "../redux/slices/areaSlice";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import useAuth from "../useAuth";
import { parseDateIntoMillisecond } from "../utills/parseDateIntoMillisecond";
import { uploadDocumentToServer } from "../utills/uploadDocumentToServer";
import {
  FaSave,
  FaRegFileAlt,
  FaMapMarkerAlt,
  FaIndustry,
  FaMoneyBillWave,
  FaPlayCircle,
  FaFileSignature,
  FaBuilding,
  FaLandmark,
  FaChartLine,
  FaClipboardCheck,
  FaCalendarAlt,
  FaUser,
  FaKey,
  FaMapPin,
  FaTools,
  FaFileContract,
  FaPercentage,
  FaUsers,
  FaHome,
  FaPiggyBank,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import LoadingComponent from "../Components/LoadingComponent";
import { NewReleases, NewReleasesSharp } from "@mui/icons-material";

const FormThemeContext = React.createContext({ focusRingColor: "ring-blue-500" });

const FormField = ({ label, error, children, className = "", icon }) => {
  const isRequired = typeof label === "string" && label.endsWith("*");
  const cleanLabel = isRequired ? label.slice(0, -1).trim() : label;

  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        {icon && <span className="text-gray-500">{icon}</span>}
        <span>
          {cleanLabel}
          {isRequired && <span className="text-red-500 ml-1 font-bold">*</span>}
        </span>
      </label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <IoClose className="text-red-500" /> {error.message}
        </p>
      )}
    </div>
  );
};

const Input = React.forwardRef(
  (
    { className = "", focusColor, defaultValue, defaultChecked, ...props },
    ref
  ) => {
    const { focusRingColor } = React.useContext(FormThemeContext);
    return (
      <input
        ref={ref}
        className={`w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 ${focusRingColor} ${className}`}
        defaultValue={defaultValue}
        defaultChecked={defaultChecked}
        {...props}
      />
    );
  }
);

const Select = React.forwardRef(
  ({ children, className = "", ...props }, ref) => {
    const { focusRingColor } = React.useContext(FormThemeContext);
    return (
      <select
        ref={ref}
        className={`w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-white ${focusRingColor} ${className}`}
        {...props}
      >
        {children}
      </select>
    );
  }
);

const TextArea = React.forwardRef(({ className = "", ...props }, ref) => {
  const { focusRingColor } = React.useContext(FormThemeContext);
  return (
    <textarea
      ref={ref}
      className={`w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 resize-none ${focusRingColor} ${className}`}
      {...props}
    />
  );
});

const normalizeFinancialYear = (fy) => {
  if (!fy) return "";
  if (fy.length === 7 && fy.includes("-")) {
    const parts = fy.split("-");
    const startYear = parts[0];
    const endYear = parts[1];
    if (startYear.length === 4 && endYear.length === 2) {
      const century = startYear.substring(0, 2);
      return `${startYear}-${century}${endYear}`;
    }
  }
  return fy;
};

const getCurrentFinancialYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  return `${startYear}-${startYear + 1}`;
};

const getFinancialYearOptions = () => {
  const years = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  const endYear = 2015;
  for (let y = startYear; y >= endYear; y--) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
};

const NewScheme = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const draftSchemeId = location.state?.draftSchemeId;
  const draftSchemeData = location.state?.draftSchemeData;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [draftScheme, setDraftScheme] = useState("");
  const [schemeId, setSchemeId] = useState(
    location.state?.draftSchemeId || null
  );

  const [proposalName, setProposalName] = useState("");

  const totalSteps = 7;


  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      // ---------------------------------------
      // Section 1: Basic Info
      // ---------------------------------------
      district: draftSchemeData?.district || "",
      ddcCumCEOName: draftSchemeData?.ddcCumCEOName || "",
      districtEngineerName: draftSchemeData?.districtEngineerName || "",
      ddo: draftSchemeData?.ddo || "",
      ddoCode: draftSchemeData?.ddoCode || "",
      financialYear: normalizeFinancialYear(draftSchemeData?.financialYear) || getCurrentFinancialYear(),
      schemeName: draftSchemeData?.schemeName || "",
      block: draftSchemeData?.block || "",
      panchayat: draftSchemeData?.panchayat || "",

      // ---------------------------------------
      // Section 2: Land Details
      // ---------------------------------------
      landAddress: draftSchemeData?.landAddress || "",
      coordinates: {
        latitude: draftSchemeData?.coordinates?.latitude || 0,
        longitude: draftSchemeData?.coordinates?.longitude || 0,
      },
      coReport: draftSchemeData?.coReport || "",
      landNature: draftSchemeData?.landNature || "",
      landSuitability: draftSchemeData?.landSuitability || "",
      geoReportDoc: draftSchemeData?.geoReportDoc || "",



      // ---------------------------------------
      // Section 4: Sector & Utility
      // ---------------------------------------
      sector: draftSchemeData?.sector || "",
      subSector: draftSchemeData?.subSector || "",
      utilityPurpose: draftSchemeData?.utilityPurpose || "",
      impactDescription: draftSchemeData?.impactDescription || "",

      // ---------------------------------------
      // Section 5: Approvals
      // ---------------------------------------
      needAssessment: draftSchemeData?.needAssessment || "",
      feasibilityStudy: draftSchemeData?.feasibilityStudy || "",
      feasibilityStudyFile: draftSchemeData?.feasibilityStudyFile || "",
      revenueGeneration: draftSchemeData?.revenueGeneration || false,
      boardApproval: draftSchemeData?.boardApproval || false,
      dpr: draftSchemeData?.dpr || "",
      dprFile: draftSchemeData?.dprFile || "",
      technicalSanctionAuthority:
        draftSchemeData?.technicalSanctionAuthority || "",
      technicalSanctionDate:
        (draftSchemeData?.technicalSanctionDate &&
          new Date(draftSchemeData?.technicalSanctionDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      technicalSanctionAmount: draftSchemeData?.technicalSanctionAmount || 0,
      administrativeApprovalAuthority:
        draftSchemeData?.administrativeApprovalAuthority
          ?.split(".")
          ?.join("-") || "",
      administrativeApprovalLetterNo: draftSchemeData?.administrativeApprovalLetterNo || "",
      administrativeApprovalFile: draftSchemeData?.administrativeApprovalFile || "",
      administrativeApprovalDate:
        (draftSchemeData?.administrativeApprovalDate &&
          new Date(draftSchemeData?.administrativeApprovalDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",


      administrativeApprovalAmount:
        draftSchemeData?.administrativeApprovalAmount || 0,

      // ---------------------------------------
      // Section 6: Financial
      // ---------------------------------------
      totalAllocated: draftSchemeData?.totalAllocated || 0,
      allocatedAmount: draftSchemeData?.allocatedAmount || 0,
      allocationLetterNo: draftSchemeData?.allocationLetterNo || "",
      totalSpent: draftSchemeData?.totalSpent || 0,
      amountRemaining: draftSchemeData?.amountRemaining || 0,

      // ---------------------------------------
      // Section 7: Execution
      // ---------------------------------------
      // tenderNoticeAttachment: draftSchemeData?.tenderNoticeAttachment || "",
      // tenderStatus: draftSchemeData?.tenderStatus || "Not Started",
      workOrderDate:
        (draftSchemeData?.workOrderDate &&
          new Date(draftSchemeData?.workOrderDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      agencyName: draftSchemeData?.agencyName || "",
      schemeStartDate:
        (draftSchemeData?.schemeStartDate &&
          new Date(draftSchemeData?.schemeStartDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      completionDate:
        (draftSchemeData?.completionDate &&
          new Date(draftSchemeData?.completionDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      schemeStatus: draftSchemeData?.schemeStatus || "Planning",
      paymentStatus: draftSchemeData?.paymentStatus || "Pending",
      ucSentLetterNo: draftSchemeData?.ucSentLetterNo || "",
      ucSentDate:
        (draftSchemeData?.ucSentDate &&
          new Date(draftSchemeData?.ucSentDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      ucAmount: draftSchemeData?.ucAmount || 0,
      progressPercentage: draftSchemeData?.progressPercentage || 0,

      // ---------------------------------------
      // Section 8: Monitoring
      // ---------------------------------------
      fieldVisitNotes: draftSchemeData?.fieldVisitNotes || [""],
      lastFieldVisitDate:
        (draftSchemeData?.lastFieldVisitDate &&
          new Date(draftSchemeData?.lastFieldVisitDate)
            ?.toISOString()
            ?.split("T")?.[0]) ||
        "",
      fieldVisitOfficials: draftSchemeData?.fieldVisitOfficials || "",
      remarks: draftSchemeData?.remarks || "",
    },
  });

  const dispatch = useDispatch();
  const { areas } = useSelector((state) => state.area);
  const { user } = useAuth();

  useEffect(() => {
    dispatch(fetchAreas());
  }, [dispatch]);

  useEffect(() => {
    if (user?.userType !== "admin" && user?.districtName) {
      setValue("district", user.districtName);
    }
  }, [user, setValue]);

  const uniqueDistricts = useMemo(() => {
    if (user?.userType !== "admin" && user?.districtName) {
      return [user.districtName];
    }
    const districts = areas.map((a) => a.district);
    return [...new Set(districts)].sort();
  }, [areas, user]);

  const selectedDistrict = watch("district");

  const effectiveDistrict = useMemo(() => {
    return (user?.userType !== "admin" ? user?.districtName : selectedDistrict)?.toUpperCase();
  }, [user, selectedDistrict]);

  const uniqueBlocks = useMemo(() => {
    if (!effectiveDistrict) return [];
    const blocks = areas
      .filter((a) => a.district === effectiveDistrict)
      .map((a) => a.block);
    return [...new Set(blocks)].sort();
  }, [areas, effectiveDistrict]);

  const selectedBlock = watch("block");

  const filteredPanchayats = useMemo(() => {
    if (!selectedBlock) return [];
    return areas
      .filter((a) => a.district === effectiveDistrict && a.block === selectedBlock)
      .map((a) => a.panchayat)
      .filter((v, i, a) => a.indexOf(v) === i) // Unique panchayats
      .sort();
  }, [areas, effectiveDistrict, selectedBlock]);

  const steps = [
    {
      id: 1,
      title: "Basic Info",
      icon: FaFileSignature,
      bgColor: "from-blue-500 to-blue-600",
      borderColor: "border-blue-200",
      textColor: "text-blue-600",
    },
    {
      id: 2,
      title: "Land Details",
      icon: FaMapMarkerAlt,
      bgColor: "from-emerald-500 to-emerald-600",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-600",
    },
    // {
    //   id: 3,
    //   title: "Bank Account",
    //   icon: FaPiggyBank,
    //   bgColor: "from-violet-500 to-violet-600",
    //   borderColor: "border-violet-200",
    //   textColor: "text-violet-600",
    // },
    {
      id: 3,
      title: "Sector & Utility",
      icon: FaIndustry,
      bgColor: "from-orange-500 to-orange-600",
      borderColor: "border-orange-200",
      textColor: "text-orange-600",
    },
    {
      id: 4,
      title: "Approvals",
      icon: FaClipboardCheck,
      bgColor: "from-amber-500 to-amber-600",
      borderColor: "border-amber-200",
      textColor: "text-amber-600",
    },
    {
      id: 5,
      title: "Financial",
      icon: FaChartLine,
      bgColor: "from-rose-500 to-rose-600",
      borderColor: "border-rose-200",
      textColor: "text-rose-600",
    },
    {
      id: 6,
      title: "Execution",
      icon: FaPlayCircle,
      bgColor: "from-indigo-500 to-indigo-600",
      borderColor: "border-indigo-200",
      textColor: "text-indigo-600",
    },
    {
      id: 7,
      title: "Monitoring",
      icon: FaRegFileAlt,
      bgColor: "from-slate-600 to-slate-700",
      borderColor: "border-slate-200",
      textColor: "text-slate-600",
    },
  ];

  const financialYearOptions = getFinancialYearOptions();

  const sectorOptions = [
    "Infrastructure - GP",
    "Asset Creation",
    "Repair/Renovation",
    "Water Supply",
    "Sanitation",
    "Road Construction",
    "Building Construction",
    "Electrical Works",
    "Other",
  ];

  const subSectorOptions = [
    "GP Bhawan - New",
    "GP Bhawan - Repair",
    "Office Renovation",
    "Dak Bungalow",
    "Bus Stand",
    "Market Complex",
    "Marriage Hall",
    "Community Hall",
    "Water Tank",
    "Drainage System",
    "Road Construction",
    "Street Lights",
    "Park Development",
    "Other",
  ];

  const landNatureOptions = [
    "Agricultural",
    "Residential",
    "Commercial",
    "Government",
    "Forest",
    "Barren",
    "Other",
  ];

  const tenderStatusOptions = [
    "Not Started",
    "Draft",
    "Published",
    "In Progress",
    "Completed",
    "Cancelled",
  ];

  const schemeStatusOptions = [
    // "Planning",
    // "Approved",
    // "Started",
    "Ongoing",
    "Completed",
    "On Hold",
    "Cancelled",
    "Partially Completed",
  ];

  const paymentStatusOptions = [
    "Pending",
    "Partial",
    "Completed",
    "Hold",
    "Rejected",
  ];

  useEffect(() => {
    if (draftSchemeId) {
      getDraftSchemeDetails();
    }
  }, []);

  const getDraftSchemeDetails = async () => {
    try {
      setDraftLoading(true);
      const { data } = await axios.get(`${server}/scheme/getById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          schemeId,
        },
      });

      setDraftScheme(data?.data);
      setSchemeId(data?.data?.id);

      // Set form values
      // Section 1: Basic Info
      // setValue("district", draftScheme?.district);
      // setValue("ddcCumCEOName", draftScheme?.ddcCumCEOName);
      // setValue("districtEngineerName", draftScheme?.wwwww);
      // setValue("ddo", draftScheme?.ddo);
      // setValue("ddoCode", draftScheme?.wwwww);
      // setValue("financialYear", draftScheme?.financialYear);
      // setValue("schemeName", draftScheme?.wwwww);
      // setValue("block", draftScheme?.wwwww);
      // setValue("panchayat", draftScheme?.wwwww);
      // setValue("panchayat", draftScheme?.wwwww);
      // // Section 2: Land Details
      // setValue("landAddress", draftScheme?.wwwww);
      // setValue("coordinates", draftScheme?.coordinates);
      // setValue("coReport", draftScheme?.wwwww);
      // setValue("landNature", draftScheme?.wwwww);
      // setValue("landSuitability", draftScheme?.wwwww);
      // // Section 3: PL Account
      // setValue("plAccountDetails", draftScheme?.plAccountDetails);
      // // Section 4: Sector & Utility
      // setValue("sector", draftScheme?.wwwww);
      // setValue("subSector", draftScheme?.wwwww);
      // setValue("utilityPurpose", draftScheme?.wwwww);
      // setValue("numberOfBeneficiaries", draftScheme?.numberOfBeneficiaries);
      // setValue("impactDescription", draftScheme?.wwwww);
      // // Section 5: Approvals
      // setValue("needAssessment", draftScheme?.wwwww);
      // setValue("feasibilityStudy", draftScheme?.wwwww);
      // setValue("revenueGeneration", draftScheme?.revenueGeneration);
      // setValue("boardApproval", draftScheme?.boardApproval);
      // setValue("dpr", draftScheme?.wwwww);
      // setValue("technicalSanctionAuthority", draftScheme?.wwwww);
      // setValue("technicalSanctionAmount", draftScheme?.technicalSanctionAmount);
      // setValue("administrativeApprovalAuthority", draftScheme?.wwwww);
      // setValue("administrativeApprovalLetterNo", draftScheme?.wwwww);
      // setValue("administrativeApprovalDate", draftScheme?.wwwww);
      // setValue(
      //   "administrativeApprovalAmount",
      //   draftScheme?.technicalSanctionAmount
      // );
      // // Section 6: Financial
      // setValue("allocatedAmount", draftScheme?.technicalSanctionAmount);
      // setValue("allocationLetterNo", draftScheme?.wwwww);
      // setValue("totalSpent", draftScheme?.totalSpent);
      // setValue("amountRemaining", draftScheme?.amountRemaining);
      // // Section 7: Execution
      // setValue("tenderStatus", draftScheme?.tenderStatus);
      // setValue("workOrderDate", draftScheme?.wwwww);
      // setValue("agencyName", draftScheme?.wwwww);
      // setValue("schemeStartDate", draftScheme?.wwwww);
      // setValue("completionDate", draftScheme?.wwwww);
      // setValue("schemeStatus", draftScheme?.schemeStatus);
      // setValue("lastMBStatus", draftScheme?.wwwww);
      // setValue("paymentStatus", draftScheme?.paymentStatus);
      // setValue("ucSentLetterNo", draftScheme?.wwwww);
      // setValue("ucSentDate", draftScheme?.ucSentDate);
      // setValue("ucAmount", draftScheme?.ucAmount);
      // setValue("progressPercentage", draftScheme?.progressPercentage);
      // // Section 8: Monitoring
      // setValue("fieldVisitNotes", draftScheme?.fieldVisitNotes);
      // setValue("lastFieldVisitDate", draftScheme?.wwwww);
      // setValue("fieldVisitOfficials", draftScheme?.wwwww);
      // setValue("remarks", draftScheme?.wwwww);
    } catch (e) {
      console.log(e?.response?.data?.message || e?.message);
    } finally {
      setDraftLoading(false);
    }
  };

  // Section 1: Create New Scheme
  const handleCreateScheme = async () => {
    setLoading(true);
    try {
      if (!proposalName.trim()) {
        return null;
      }
      const { data: resData } = await axios.post(
        `${server}/scheme/new`,
        {
          proposalName: proposalName,
          schemeType: "new",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSchemeId(resData?.schemeId);

      toast.success("New proposal created");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);

    } finally {
      setLoading(false);
    }
  };

  // Save Draft for all sections
  const handleSaveDraft = async (isFinalSubmit = false) => {
    console.log("isFinalSubmit ", isFinalSubmit);

    if (!schemeId && currentStep === 1) {
      toast.error("Please create the scheme first");
      return;
    }

    setLoading(true);
    try {
      let updateData = {};
      updateData = {
        // Section 1
        district: getValues("district"),
        ddcCumCEOName: getValues("ddcCumCEOName"),
        districtEngineerName: getValues("districtEngineerName"),
        ddo: getValues("ddo"),
        ddoCode: getValues("ddoCode"),
        financialYear: getValues("financialYear"),
        schemeName: getValues("schemeName"),
        block: getValues("block"),
        panchayat: getValues("panchayat"),
        panchayat: getValues("panchayat"),

        // Section 2
        landAddress: getValues("landAddress"),
        coordinates: {
          latitude: parseFloat(getValues("coordinates.latitude")) || 0,
          longitude: parseFloat(getValues("coordinates.longitude")) || 0,
        },
        coReport: getValues("coReport"),
        landNature: getValues("landNature"),
        landSuitability: getValues("landSuitability"),
        geoReportDoc: getValues("geoReportDoc"),


        // Section 4
        sector: getValues("sector"),
        subSector: getValues("subSector"),
        utilityPurpose: getValues("utilityPurpose"),
        impactDescription: getValues("impactDescription"),
        // Section 5
        needAssessment: getValues("needAssessment"),
        feasibilityStudy: getValues("feasibilityStudy"),
        feasibilityStudyFile: getValues("feasibilityStudyFile"),
        revenueGeneration: getValues("revenueGeneration"),
        boardApproval: getValues("boardApproval"),
        dpr: getValues("dpr"),
        dprFile: getValues("dprFile"),
        technicalSanctionAuthority: getValues("technicalSanctionAuthority"),
        technicalSanctionDate: getValues("technicalSanctionDate") || null,
        technicalSanctionAmount:
          parseFloat(getValues("technicalSanctionAmount")) || 0,
        administrativeApprovalAuthority: getValues("administrativeApprovalAuthority"),
        administrativeApprovalLetterNo: getValues("administrativeApprovalLetterNo"),
        administrativeApprovalFile: getValues("administrativeApprovalFile"),
        administrativeApprovalDate: getValues("administrativeApprovalDate") || null,
        administrativeApprovalAmount:
          parseFloat(getValues("administrativeApprovalAmount")) || 0,
        // Section 6
        totalAllocated: parseFloat(getValues("totalAllocated")) || 0,
        allocatedAmount: parseFloat(getValues("allocatedAmount")) || 0,
        allocationLetterNo: getValues("allocationLetterNo"),
        amountRemaining: parseFloat(getValues("amountRemaining")) || 0,
        totalSpent: parseFloat(getValues("totalSpent")) || 0,
        // Section 7
        // tenderNoticeAttachment: getValues("tenderNoticeAttachment"),
        // tenderStatus: getValues("tenderStatus"),
        workOrderDate: getValues("workOrderDate") || null,
        agencyName: getValues("agencyName"),
        schemeStartDate: getValues("schemeStartDate") || null,
        completionDate: getValues("completionDate") || null,
        schemeStatus: getValues("schemeStatus"),
        paymentStatus: getValues("paymentStatus"),
        ucSentLetterNo: getValues("ucSentLetterNo"),
        ucSentDate: getValues("ucSentDate") || null,
        ucAmount: parseFloat(getValues("ucAmount")) || 0,
        progressPercentage: parseFloat(getValues("progressPercentage")) || 0,

        // Section 8
        // photos: photoData,
        fieldVisitNotes: notes,
        lastFieldVisitDate: getValues("lastFieldVisitDate") || null,
        fieldVisitOfficials: getValues("fieldVisitOfficials"),
        remarks: getValues("remarks"),
        formStatus: isFinalSubmit ? "completed" : "draft",
        schemeType: "new",
        CurrentStage: currentStep,
      };
      // const photoData = photos.map((photo) => ({
      //   url: photo.url,
      //   lat: photo.lat,
      //   long: photo.long,
      //   fileSize: photo.fileSize,
      //   dateTime: photo.dateTime,
      // }));

      if (schemeId) {
        const { data: resData } = await axios.put(
          `${server}/scheme/updateById`,
          updateData,
          {
            params: {
              schemeId,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (resData.success) {
          toast.success("Saved!");
          setCurrentStep(currentStep);

          if (isFinalSubmit) {
            navigate("../schemeDetails", {
              state: {
                schemeId: schemeId,
              },
            });
          }
        }
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft.");
    } finally {
      setLoading(false);
    }
  };

  const watchedLandAddress = watch("landAddress");
  const watchedLatitude = watch("coordinates.latitude");
  const watchedLongitude = watch("coordinates.longitude");
  const watchedCoReport = watch("coReport");
  const watchedLandNature = watch("landNature");
  const watchedLandSuitability = watch("landSuitability");
  const watchedGeoReportDoc = watch("geoReportDoc");

  const watchedSector = watch("sector");
  const watchedSubSector = watch("subSector");
  const watchedUtilityPurpose = watch("utilityPurpose");
  const watchedImpactDescription = watch("impactDescription");

  const watchedNeedAssessment = watch("needAssessment");
  const watchedFeasibilityStudy = watch("feasibilityStudy");
  const watchedFeasibilityStudyFile = watch("feasibilityStudyFile");
  const watchedDpr = watch("dpr");
  const watchedDprFile = watch("dprFile");
  const watchedTechnicalSanctionAuthority = watch("technicalSanctionAuthority");
  const watchedTechnicalSanctionDate = watch("technicalSanctionDate");
  const watchedTechnicalSanctionAmount = watch("technicalSanctionAmount");
  const watchedAdministrativeApprovalAuthority = watch("administrativeApprovalAuthority");
  const watchedAdministrativeApprovalLetterNo = watch("administrativeApprovalLetterNo");
  const watchedAdministrativeApprovalFile = watch("administrativeApprovalFile");
  const watchedAdministrativeApprovalDate = watch("administrativeApprovalDate");
  const watchedAdministrativeApprovalAmount = watch("administrativeApprovalAmount");

  const isFormReadyForFinalSubmit = () => {
    // Check Land Details
    const landFilled =
      watchedLandAddress && watchedLandAddress.toString().trim() !== "" &&
      watchedLatitude !== undefined && watchedLatitude !== null && watchedLatitude !== "" &&
      watchedLongitude !== undefined && watchedLongitude !== null && watchedLongitude !== "" &&
      watchedCoReport && watchedCoReport.toString().trim() !== "" &&
      watchedLandNature && watchedLandNature.toString().trim() !== "" &&
      watchedLandSuitability && watchedLandSuitability.toString().trim() !== "" &&
      watchedGeoReportDoc && watchedGeoReportDoc.toString().trim() !== "";

    // Check Sector & Utility
    const sectorFilled =
      watchedSector && watchedSector.toString().trim() !== "" &&
      watchedSubSector && watchedSubSector.toString().trim() !== "" &&
      watchedUtilityPurpose && watchedUtilityPurpose.toString().trim() !== "" &&
      watchedImpactDescription && watchedImpactDescription.toString().trim() !== "";

    // Check Approvals
    const approvalsFilled =
      watchedNeedAssessment && watchedNeedAssessment.toString().trim() !== "" &&
      watchedFeasibilityStudy && watchedFeasibilityStudy.toString().trim() !== "" &&
      watchedFeasibilityStudyFile && watchedFeasibilityStudyFile.toString().trim() !== "" &&
      watchedDpr && watchedDpr.toString().trim() !== "" &&
      watchedDprFile && watchedDprFile.toString().trim() !== "" &&
      watchedTechnicalSanctionAuthority && watchedTechnicalSanctionAuthority.toString().trim() !== "" &&
      watchedTechnicalSanctionDate && watchedTechnicalSanctionDate.toString().trim() !== "" &&
      watchedTechnicalSanctionAmount !== undefined && watchedTechnicalSanctionAmount !== null && watchedTechnicalSanctionAmount !== "" &&
      watchedAdministrativeApprovalAuthority && watchedAdministrativeApprovalAuthority.toString().trim() !== "" &&
      watchedAdministrativeApprovalLetterNo && watchedAdministrativeApprovalLetterNo.toString().trim() !== "" &&
      watchedAdministrativeApprovalFile && watchedAdministrativeApprovalFile.toString().trim() !== "" &&
      watchedAdministrativeApprovalDate && watchedAdministrativeApprovalDate.toString().trim() !== "" &&
      watchedAdministrativeApprovalAmount !== undefined && watchedAdministrativeApprovalAmount !== null && watchedAdministrativeApprovalAmount !== "";

    return !!(landFilled && sectorFilled);
  };

  // Final Submit
  const handleFinalSubmit = async () => {
    let userRes = window.confirm("Are you sure, you want to final submit?");

    if (!userRes) return null;

    await handleSaveDraft(true);
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    const newPhotos = files.map((file) => ({
      url: URL.createObjectURL(file),
      file,
      lat: parseFloat(getValues("coordinates.latitude")) || 0,
      long: parseFloat(getValues("coordinates.longitude")) || 0,
      fileSize: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      dateTime: new Date().toISOString(),
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const [notes, setNotes] = useState(getValues("fieldVisitNotes") || [""]);
  const addFieldVisitNote = () => {
    const updated = [...notes, ""];
    setNotes(updated);
    setValue("fieldVisitNotes", updated);
  };

  const removeFieldVisitNote = (index) => {
    const updated = notes.filter((_, i) => i !== index);
    setNotes(updated);
    setValue("fieldVisitNotes", updated);
  };

  const updateFieldVisitNote = (index, value) => {
    const updated = [...notes];
    updated[index] = value;
    setNotes(updated);
    setValue("fieldVisitNotes", updated);
  };

  // Get current step color
  const getCurrentStepColor = () => {
    const currentStepData = steps[currentStep - 1];
    return currentStepData ? currentStepData.textColor : "text-blue-600";
  };

  // Render sections
  const renderSection = () => {
    const CurrentStepIcon = steps[currentStep - 1]?.icon || FaFileSignature;
    const currentStepColor = getCurrentStepColor();

    switch (currentStep) {
      case 1:
        return (
          <motion.div
            key="section-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[0].title}
              </h2>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <FormField
                label="District/Zilla Parishad *"
                error={errors.district}
                icon={<FaBuilding />}
              >
                <Select
                  {...register("district", {
                    required: "District is required",
                  })}
                  disabled={user?.userType !== "admin"}
                >
                  <option value="">Select District</option>
                  {uniqueDistricts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField
                label="DDC-cum-CEO Name *"
                error={errors.ddcCumCEOName}
                icon={<FaUser />}
              >
                <Input
                  type="text"
                  {...register("ddcCumCEOName", {
                    required: "DDC-cum-CEO name is required",
                  })}
                  placeholder="Enter full name"
                />
              </FormField>

              <FormField
                label="District Engineer Name *"
                error={errors.districtEngineerName}
                icon={<FaUser />}
              >
                <Input
                  type="text"
                  {...register("districtEngineerName", {
                    required: "District Engineer name is required",
                  })}
                  placeholder="Enter full name"
                />
              </FormField>

              <FormField label="DDO *" error={errors.ddo} icon={<FaUser />}>
                <Input
                  type="text"
                  {...register("ddo", { required: "DDO is required" })}
                  placeholder="Enter DDO name"
                />
              </FormField>

              <FormField
                label="DDO Code *"
                error={errors.ddoCode}
                icon={<FaKey />}
              >
                <Input
                  type="text"
                  {...register("ddoCode", { required: "DDO code is required" })}
                  placeholder="Enter DDO code"
                />
              </FormField>

              <FormField
                label="Financial Year *"
                error={errors.financialYear}
                icon={<FaCalendarAlt />}
              >
                <Select
                  {...register("financialYear", {
                    required: "Financial year is required",
                  })}
                >
                  <option value="">Select Year</option>
                  {financialYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField
                label="Scheme Name *"
                error={errors.schemeName}
                icon={<FaFileSignature />}
              >
                <Input
                  type="text"
                  {...register("schemeName", {
                    required: "Scheme name is required",
                  })}
                  placeholder="Enter scheme name"
                />
              </FormField>

              <FormField
                label="Block *"
                error={errors.block}
                icon={<FaMapPin />}
              >
                <Select
                  {...register("block", { required: "Block is required" })}
                >
                  <option value="">Select Block</option>
                  {uniqueBlocks.map((block) => (
                    <option key={block} value={block}>
                      {block}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Area/Ward" icon={<FaHome />}>
                <Input
                  type="text"
                  {...register("panchayat")}
                  placeholder="Enter Area/Ward"
                />
              </FormField>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="section-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[1].title}
              </h2>
              <p className="text-gray-600 text-base">
                Enter land location and details
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <FormField label="Land Address *" icon={<FaMapMarkerAlt />}>
                  <TextArea
                    {...register("landAddress")}
                    rows={2}
                    placeholder="Enter complete land address with landmarks"
                  />
                </FormField>
              </div>

              <FormField label="Latitude *" icon={<FaMapPin />}>
                <Input
                  type="number"
                  step="any"
                  {...register("coordinates.latitude")}
                  placeholder="e.g., 23.6139"
                />
              </FormField>

              <FormField label="Longitude *" icon={<FaMapPin />}>
                <Input
                  type="number"
                  step="any"
                  {...register("coordinates.longitude")}
                  placeholder="e.g., 84.2090"
                />
              </FormField>

              <FormField label="Land Nature *" icon={<FaLandmark />}>
                <Select {...register("landNature")}>
                  <option value="">Select Land Nature</option>
                  {landNatureOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Land Suitability *" icon={<FaLandmark />}>
                <Input
                  type="text"
                  {...register("landSuitability")}
                  placeholder="Describe land suitability"
                />
              </FormField>

              <div className="md:col-span-1">
                <FormField label="CO Report *" icon={<FaRegFileAlt />}>
                  <TextArea
                    {...register("coReport")}
                    rows={2}
                    placeholder="Enter CO report details and findings"
                  />
                </FormField>
              </div>

              <div className="md:col-span-1">
                <FormField label="Geo Report *" icon={<FaRegFileAlt />}>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="geoReportDocInput"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const url = await uploadDocumentToServer(file);
                          if (url) {
                            setValue("geoReportDoc", url);
                            toast.success("Geo Report uploaded successfully");
                          }
                          setLoading(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="geoReportDocInput"
                      className="cursor-pointer bg-slate-100 text-slate-700 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-200 text-center font-medium transition-colors"
                    >
                      {watch("geoReportDoc") ? "Re-upload Report" : "Upload Report"}
                    </label>
                    {watch("geoReportDoc") && (
                      <a
                        href={watch("geoReportDoc")}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-sm text-center flex items-center justify-center gap-1"
                      >
                        <FaRegFileAlt /> View Uploaded Report
                      </a>
                    )}
                  </div>
                </FormField>
              </div>
            </div>
          </motion.div>
        );



      case 3:
        return (
          <motion.div
            key="section-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[2].title}
              </h2>
              <p className="text-gray-600 text-base">
                Enter sector and utility details
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <FormField
                label="Sector *"
                error={errors.sector}
                icon={<FaIndustry />}
              >
                <Select
                  {...register("sector", { required: "Sector is required" })}
                >
                  <option value="">Select Sector</option>
                  {sectorOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField
                label="Sub Sector *"
                error={errors.subSector}
                icon={<FaTools />}
              >
                <Select
                  {...register("subSector", {
                    required: "Sub sector is required",
                  })}
                >
                  <option value="">Select Sub Sector</option>
                  {subSectorOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField>


              {/* Removed Fields */}

              {/* <FormField label="Number of Beneficiaries" icon={<FaUsers />}>
                <Input
                  type="number"
                  {...register("numberOfBeneficiaries")}
                  placeholder="Estimated number of beneficiaries"
                />
              </FormField> */}

              <div className="md:col-span-3 grid grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <FormField label="Utility Purpose *" icon={<FaIndustry />}>
                    <TextArea
                      {...register("utilityPurpose")}
                      rows={3}
                      placeholder="Describe the purpose and utility of the scheme"
                    />
                  </FormField>
                </div>

                <div className="md:col-span-2">
                  <FormField label="Impact Description *" icon={<FaChartLine />}>
                    <TextArea
                      {...register("impactDescription")}
                      rows={3}
                      placeholder="Describe the expected social and economic impact"
                    />
                  </FormField>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            key="section-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[3].title}
              </h2>
              <p className="text-gray-600 text-base">
                Enter approval process details
              </p>
            </div>

            <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6">
              {/* Row 0, Text fields */}
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Need Assessment" icon={<FaRegFileAlt />}>
                  <Input type="text" {...register("needAssessment")} placeholder="Need Assessment" />
                </FormField>
                <FormField label="Feasibility Study" icon={<FaRegFileAlt />}>
                  <Input type="text" {...register("feasibilityStudy")} placeholder="Feasibility Study" />
                </FormField>
                <FormField label="DPR" icon={<FaRegFileAlt />}>
                  <Input type="text" {...register("dpr")} placeholder="DPR" />
                </FormField>
              </div>

              {/* Row 1, Column 1 */}
              <div className="md:col-span-3 flex flex-col gap-4">
                <FormField label="Assessment / Feasibility Study File" icon={<FaRegFileAlt />}>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="feasibilityStudyFileInput"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const url = await uploadDocumentToServer(file);
                          if (url) {
                            setValue("feasibilityStudyFile", url);
                            toast.success("Document uploaded successfully");
                          }
                          setLoading(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="feasibilityStudyFileInput"
                      className="cursor-pointer bg-slate-100 text-slate-700 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-200 text-center font-medium transition-colors"
                    >
                      {watch("feasibilityStudyFile") ? "Re-upload File" : "Upload File"}
                    </label>
                    {watch("feasibilityStudyFile") && (
                      <a
                        href={watch("feasibilityStudyFile")}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-sm text-center flex items-center justify-center gap-1"
                      >
                        <FaRegFileAlt /> View Uploaded File
                      </a>
                    )}
                  </div>
                </FormField>
              </div>

              {/* Row 1, Column 2 */}
              <div className="md:col-span-3 flex flex-col gap-4">
                <FormField
                  label="Detailed Project Report (DPR) File"
                  icon={<FaRegFileAlt />}
                >
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="dprFileInput"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const url = await uploadDocumentToServer(file);
                          if (url) {
                            setValue("dprFile", url);
                            toast.success("DPR uploaded successfully");
                          }
                          setLoading(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="dprFileInput"
                      className="cursor-pointer bg-slate-100 text-slate-700 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-200 text-center font-medium transition-colors"
                    >
                      {watch("dprFile") ? "Re-upload DPR" : "Upload DPR"}
                    </label>
                    {watch("dprFile") && (
                      <a
                        href={watch("dprFile")}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-sm text-center flex items-center justify-center gap-1"
                      >
                        <FaRegFileAlt /> View Uploaded DPR
                      </a>
                    )}
                  </div>
                </FormField>
              </div>

              {/* Row 1, Column 3 */}
              <div className="md:col-span-3  flex flex-col gap-4">
                <FormField
                  label="Technical Sanction Authority"
                  icon={<FaClipboardCheck />}
                >
                  <Input
                    type="text"
                    {...register("technicalSanctionAuthority")}
                    placeholder="Authority name"
                  />
                </FormField>

                <FormField
                  label="Technical Sanction Date"
                  icon={<FaCalendarAlt />}
                >
                  <Input
                    type="date"
                    {...register("technicalSanctionDate")}
                  />
                </FormField>

                <FormField
                  label="Technical Sanction Amount"
                  icon={<FaMoneyBillWave />}
                >
                  <Input
                    type="number"
                    step="0.01"
                    {...register("technicalSanctionAmount")}
                    placeholder="0"
                  />
                </FormField>
              </div>

              {/* Admin Approval Section (Full Width, 4 Columns inner) */}
              <div className="md:col-span-3 border-t border-gray-100 pt-6 mt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <FormField
                  label="Admin Approval Authority"
                  icon={<FaClipboardCheck />}
                >
                  <Input
                    type="text"
                    {...register("administrativeApprovalAuthority")}
                    placeholder="Approval authority name"
                  />
                </FormField>

                <FormField
                  label="Admin Approval Letter No"
                  icon={<FaRegFileAlt />}
                >
                  <Input
                    type="text"
                    {...register("administrativeApprovalLetterNo")}
                    placeholder="Letter number"
                  />
                </FormField>

                <FormField
                  label="Admin Approval File"
                  icon={<FaRegFileAlt />}
                >
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="adminApprovalInput"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const url = await uploadDocumentToServer(file);
                          if (url) {
                            setValue("administrativeApprovalFile", url);
                            toast.success("Admin Approval uploaded successfully");
                          }
                          setLoading(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="adminApprovalInput"
                      className="cursor-pointer bg-slate-100 text-slate-700 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-200 text-center font-medium transition-colors"
                    >
                      {watch("administrativeApprovalFile") ? "Re-upload File" : "Upload File"}
                    </label>
                    {watch("administrativeApprovalFile") && (
                      <a
                        href={watch("administrativeApprovalFile")}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-sm text-center flex items-center justify-center gap-1"
                      >
                        <FaRegFileAlt /> View Uploaded File
                      </a>
                    )}
                  </div>
                </FormField>

                <FormField label="Admin Approval Date" icon={<FaCalendarAlt />}>
                  <Input
                    type="date"
                    {...register("administrativeApprovalDate")}
                  />
                </FormField>

                <FormField
                  label="Admin Approval Amount"
                  icon={<FaMoneyBillWave />}
                >
                  <Input
                    type="number"
                    step="0.01"
                    {...register("administrativeApprovalAmount")}
                    placeholder="0"
                  />
                </FormField>
              </div>

              {/* Checkboxes Wrapper */}
              <div className="md:col-span-3 flex flex-wrap gap-8 mt-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    {...register("revenueGeneration")}
                    className="h-5 w-5 rounded border-gray-300 focus:ring-2 focus:ring-amber-500"
                  />
                  Revenue Generation Expected
                </label>

                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    {...register("boardApproval")}
                    className="h-5 w-5 rounded border-gray-300 focus:ring-2 focus:ring-amber-500"
                  />
                  Board Approval Received
                </label>
              </div>
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            key="section-5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[4].title}
              </h2>
              <p className="text-gray-600 text-base">Enter financial details</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <FormField
                label="Total Allocated *"
                error={errors.totalAllocated}
                icon={<FaMoneyBillWave />}
              >
                <Input
                  type="number"
                  step="0.01"
                  {...register("totalAllocated", {
                    required: "Total allocated is required",
                  })}
                  placeholder="Amount in ₹"
                />
              </FormField>

              <FormField
                label="Allocated Amount *"
                error={errors.allocatedAmount}
                icon={<FaMoneyBillWave />}
              >
                <Input
                  type="number"
                  step="0.01"
                  {...register("allocatedAmount", {
                    required: "Allocated amount is required",
                  })}
                  placeholder="Amount in ₹"
                />
              </FormField>

              <FormField label="Allocation Letter No" icon={<FaRegFileAlt />}>
                <Input
                  type="text"
                  {...register("allocationLetterNo")}
                  placeholder="Letter number"
                />
              </FormField>

              <FormField label="Amount Remaining" icon={<FaMoneyBillWave />}>
                <Input
                  type="number"
                  step="0.01"
                  {...register("amountRemaining")}
                  placeholder="Remaining amount in ₹"
                />
              </FormField>

              <FormField label="Total Spent" icon={<FaMoneyBillWave />}>
                <Input
                  type="number"
                  step="0.01"
                  {...register("totalSpent")}
                  placeholder="Amount spent in ₹"
                />
              </FormField>
            </div>
          </motion.div>
        );

      case 6:
        return (
          <motion.div
            key="section-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[5].title}
              </h2>
              <p className="text-gray-600 text-base">Enter execution details</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {/* <FormField
                label="Tender Notice Attachment"
                icon={<FaRegFileAlt />}
              >
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    id="tenderNoticeInput"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setLoading(true);
                        const url = await uploadDocumentToServer(file);
                        if (url) {
                          setValue("tenderNoticeAttachment", url);
                          toast.success("Document uploaded successfully");
                        }
                        setLoading(false);
                      }
                    }}
                  />
                  <label
                    htmlFor="tenderNoticeInput"
                    className="cursor-pointer bg-slate-100 text-slate-700 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-200 text-center font-medium transition-colors"
                  >
                    {watch("tenderNoticeAttachment") ? "Re-upload File" : "Upload File"}
                  </label>
                  {watch("tenderNoticeAttachment") && (
                    <a
                      href={watch("tenderNoticeAttachment")}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline text-sm text-center flex items-center justify-center gap-1"
                    >
                      <FaRegFileAlt /> View Uploaded File
                    </a>
                  )}
                </div>
              </FormField> */}
              {/* 
              <FormField label="Tender Status" icon={<FaFileContract />}>
                <Select {...register("tenderStatus")}>
                  {tenderStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField> */}

              <FormField label="Work Order Date" icon={<FaCalendarAlt />}>
                <Input type="date" {...register("workOrderDate")} />
              </FormField>

              <FormField label="Agency Name" icon={<FaBuilding />}>
                <Input
                  type="text"
                  {...register("agencyName")}
                  placeholder="Contractor/agency name"
                />
              </FormField>

              <FormField label="Scheme Start Date" icon={<FaCalendarAlt />}>
                <Input type="date" {...register("schemeStartDate")} />
              </FormField>

              <FormField label="Completion Date" icon={<FaCalendarAlt />}>
                <Input type="date" {...register("completionDate")} />
              </FormField>

              <FormField label="Scheme Status" icon={<FaPlayCircle />}>
                <Select {...register("schemeStatus")}>
                  {schemeStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField>

              {/* Removed Last MB Status */}

              <FormField label="Payment Status" icon={<FaMoneyBillWave />}>
                <Select {...register("paymentStatus")}>
                  {paymentStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="UC Sent Letter No" icon={<FaRegFileAlt />}>
                <Input
                  type="text"
                  {...register("ucSentLetterNo")}
                  placeholder="Utilization certificate letter no"
                />
              </FormField>

              <FormField label="UC Sent Date" icon={<FaCalendarAlt />}>
                <Input type="date" {...register("ucSentDate")} />
              </FormField>

              <FormField label="UC Amount" icon={<FaMoneyBillWave />}>
                <Input
                  type="number"
                  step="0.01"
                  {...register("ucAmount")}
                  placeholder="UC amount in ₹"
                />
              </FormField>

              <FormField label="Progress Percentage" icon={<FaPercentage />}>
                <Input
                  type="number"
                  step="0.1"
                  {...register("progressPercentage")}
                  placeholder="Progress in %"
                  min="0"
                  max="100"
                />
              </FormField>
            </div>
          </motion.div>
        );

      case 7:
        return (
          <motion.div
            key="section-7"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-left mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {steps[6].title}
              </h2>
              <p className="text-gray-600 text-base">
                Enter monitoring details and upload documents
              </p>
            </div>


            {/* Field Visit Notes */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="font-semibold flex items-center gap-2">
                  <FaRegFileAlt /> Field Visit Notes
                </label>

                <button
                  type="button"
                  onClick={addFieldVisitNote}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
                >
                  + Add Note
                </button>
              </div>

              {notes.map((note, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    className="border px-3 py-2 rounded-lg w-full"
                    value={note}
                    onChange={(e) =>
                      updateFieldVisitNote(index, e.target.value)
                    }
                    placeholder="Enter field visit observation..."
                  />

                  {notes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFieldVisitNote(index)}
                      className="px-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100"
                    >
                      <IoClose size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <FormField label="Last Field Visit Date" icon={<FaCalendarAlt />}>
                <Input type="date" {...register("lastFieldVisitDate")} />
              </FormField>

              <FormField label="Field Visit Officials" icon={<FaUser />}>
                <Input
                  type="text"
                  {...register("fieldVisitOfficials")}
                  placeholder="e.g., DE, AE, JE, DDC"
                />
              </FormField>

              <div className="md:col-span-2">
                <FormField label="Remarks" icon={<FaRegFileAlt />}>
                  <TextArea
                    {...register("remarks")}
                    rows={4}
                    placeholder="Any additional remarks or notes..."
                  />
                </FormField>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  // const handleBulkUpload = async () => {
  //   try {
  //     for (let i = 0; i < prdSchemes.length; i++) {
  //       const { data: resData } = await axios.post(
  //         `${server}/scheme/new`,
  //         {
  //           // ---------------------------------------
  //           // Section 1: Basic Info
  //           // ---------------------------------------
  //           district: prdSchemes?.[i]?.district || "",
  //           ddcCumCEOName: prdSchemes?.[i]?.ddcCumCEOName || "",
  //           districtEngineerName: prdSchemes?.[i]?.districtEngineerName || "",
  //           ddo: prdSchemes?.[i]?.ddo || "",
  //           ddoCode: prdSchemes?.[i]?.ddoCode || "",
  //           financialYear: prdSchemes?.[i]?.financialYear || "2025-26",
  //           schemeName: prdSchemes?.[i]?.schemeName || "",
  //           block: prdSchemes?.[i]?.block || "",
  //           panchayat: prdSchemes?.[i]?.panchayat || "",

  //           // ---------------------------------------
  //           // Section 2: Land Details
  //           // ---------------------------------------
  //           landAddress: prdSchemes?.[i]?.landAddress || "",
  //           coordinates: {
  //             latitude: prdSchemes?.[i]?.coordinates?.latitude || 0,
  //             longitude: prdSchemes?.[i]?.coordinates?.longitude || 0,
  //           },
  //           coReport: prdSchemes?.[i]?.coReport || "",
  //           landNature: prdSchemes?.[i]?.landNature || "",
  //           landSuitability: prdSchemes?.[i]?.landSuitability || "",
  //           geoReportDoc: prdSchemes?.[i]?.geoReportDoc || "",


  //           // ---------------------------------------
  //           // Section 4: Sector & Utility
  //           // ---------------------------------------
  //           sector: prdSchemes?.[i]?.sector || "",
  //           subSector: prdSchemes?.[i]?.subSector || "",
  //           utilityPurpose: prdSchemes?.[i]?.utilityPurpose || "",
  //           impactDescription: prdSchemes?.[i]?.impactDescription || "",

  //           // ---------------------------------------
  //           // Section 5: Approvals
  //           // ---------------------------------------
  //           needAssessment: prdSchemes?.[i]?.needAssessment || "",
  //           feasibilityStudyFile: prdSchemes?.[i]?.feasibilityStudyFile || "",
  //           revenueGeneration: prdSchemes?.[i]?.revenueGeneration || false,
  //           boardApproval: prdSchemes?.[i]?.boardApproval || false,
  //           drpFile: prdSchemes?.[i]?.drpFile || "",
  //           technicalSanctionAuthority:
  //             prdSchemes?.[i]?.technicalSanctionAuthority || "",
  //           technicalSanctionAmount:
  //             prdSchemes?.[i]?.technicalSanctionAmount || 0,
  //           administrativeApprovalAuthority:
  //             prdSchemes?.[i]?.administrativeApprovalAuthority || "",
  //           administrativeApprovalLetterNo:
  //             prdSchemes?.[i]?.administrativeApprovalLetterNo || "",
  //           administrativeApprovalDate:
  //             (prdSchemes?.[i]?.administrativeApprovalDate &&
  //               new Date(prdSchemes?.[i]?.administrativeApprovalDate)
  //                 ?.toISOString()
  //                 ?.split("T")?.[0]) ||
  //             "",
  //           administrativeApprovalAmount:
  //             prdSchemes?.[i]?.administrativeApprovalAmount || 0,

  //           // ---------------------------------------
  //           // Section 6: Financial
  //           // ---------------------------------------
  //           allocatedAmount: prdSchemes?.[i]?.allocatedAmount || 0,
  //           allocationLetterNo: prdSchemes?.[i]?.allocationLetterNo || "",
  //           totalSpent: prdSchemes?.[i]?.totalSpent || 0,
  //           amountRemaining: prdSchemes?.[i]?.amountRemaining || 0,

  //           // ---------------------------------------
  //           // Section 7: Execution
  //           // ---------------------------------------
  //           tenderStatus: prdSchemes?.[i]?.tenderStatus || "Not Started",
  //           workOrderDate: null,
  //           // prdSchemes?.[i]?.workOrderDate &&
  //           // parseDateIntoMillisecond(
  //           //   prdSchemes?.[i]?.workOrderDate?.split(".")?.reverse()?.join("-")
  //           // ),
  //           agencyName: prdSchemes?.[i]?.agencyName || "",
  //           schemeStartDate: null,
  //           // prdSchemes?.[i]?.schemeStartDate &&
  //           // parseDateIntoMillisecond(
  //           //   prdSchemes?.[i]?.schemeStartDate
  //           //     ?.split(".")
  //           //     ?.reverse()
  //           //     ?.join("-")
  //           // ),
  //           completionDate: null,
  //           // prdSchemes?.[i]?.completionDate &&
  //           // parseDateIntoMillisecond(
  //           //   prdSchemes?.[i]?.completionDate
  //           //     ?.split(".")
  //           //     ?.reverse()
  //           //     ?.join("-")
  //           // ),
  //           schemeStatus: prdSchemes?.[i]?.schemeStatus || "Planning",
  //           paymentStatus: prdSchemes?.[i]?.paymentStatus || "Pending",
  //           ucSentLetterNo: prdSchemes?.[i]?.ucSentLetterNo || "",
  //           ucSentDate:
  //             (prdSchemes?.[i]?.ucSentDate &&
  //               new Date(prdSchemes?.[i]?.ucSentDate)
  //                 ?.toISOString()
  //                 ?.split("T")?.[0]) ||
  //             "",
  //           ucAmount: prdSchemes?.[i]?.ucAmount || 0,
  //           progressPercentage: prdSchemes?.[i]?.progressPercentage || 0,

  //           // ---------------------------------------
  //           // Section 8: Monitoring
  //           // ---------------------------------------
  //           fieldVisitNotes: prdSchemes?.[i]?.fieldVisitNotes || [""],
  //           lastFieldVisitDate:
  //             (prdSchemes?.[i]?.lastFieldVisitDate &&
  //               new Date(prdSchemes?.[i]?.lastFieldVisitDate)
  //                 ?.toISOString()
  //                 ?.split("T")?.[0]) ||
  //             "",
  //           fieldVisitOfficials: prdSchemes?.[i]?.fieldVisitOfficials || "",
  //           remarks: prdSchemes?.[i]?.remarks || "",
  //         },
  //         {
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem("token")}`,
  //           },
  //         }
  //       );
  //     }
  //   } catch (e) {
  //     console.log(e?.response?.data?.message || e?.message);
  //   }
  // };
  const currentStepData = steps[currentStep - 1];
  const focusRingColor = currentStepData
    ? currentStepData.textColor.replace("text-", "ring-")
    : "ring-blue-500";

  return (
    <FormThemeContext.Provider value={{ focusRingColor }}>
      <div className="min-h-screen py-4 px-4">

        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="w-full flex justify-between">
            <div className="text-left mb-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Create New Proposal
              </h1>
              <p className="text-base text-gray-600">
                Complete all {totalSteps} sections to register your scheme
              </p>
            </div>

            <div className="flex flex-col items-end gap-1">
              <button
                className={`px-5 py-2.5 font-medium rounded-lg shadow-sm transition-all focus:ring-2 ${isFormReadyForFinalSubmit()
                  ? "bg-rose-600 hover:bg-rose-700 text-white focus:ring-rose-300 active:scale-95 cursor-pointer"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                onClick={handleFinalSubmit}
                disabled={!isFormReadyForFinalSubmit()}
                title={
                  !isFormReadyForFinalSubmit()
                    ? "Please fill all fields in Land Details, and Sector & Utility sections to enable Final Submit."
                    : ""
                }
              >
                Final Submit
              </button>
              {!isFormReadyForFinalSubmit() && (
                <span className="text-xs text-rose-600 font-medium">
                  * Fill Land and Sector details to enable
                </span>
              )}
            </div>
          </div>

          {/* Steps Indicator */}
          <div className="mb-8">
            <div className="flex justify-between relative">
              {/* Connecting Lines */}
              <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 -z-10 mx-16"></div>

              {steps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center relative z-10"
                  >
                    <button
                      onClick={() => {
                        setCurrentStep(step.id);
                        const newProgress =
                          ((step.id - 1) / (totalSteps - 1)) * 100;
                      }}
                      className={`w-14 h-14 hover:scale-105 rounded-2xl flex items-center justify-center text-white font-bold text-lg transition-all duration-300 ${currentStep === step.id
                        ? `bg-gradient-to-r bg-blue-600 ring-4 ring-opacity-30 scale-110`
                        : "bg-gradient-to-r from-green-600 to-green-700"
                        }`}
                    >
                      {<StepIcon />}
                    </button>
                    <span
                      className={`mt-3 text-sm font-medium ${currentStep >= step.id ? "text-gray-900" : "text-gray-500"
                        }`}
                    >
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Container */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
            <div className="p-8">
              {!draftLoading && (
                <AnimatePresence mode="wait">{renderSection()}</AnimatePresence>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            {/* {currentStep < totalSteps ? (
              <> */}
            <button
              onClick={() => handleSaveDraft(false)}
              disabled={loading}
              className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 flex items-center gap-3 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50"
            >
              <FaSave /> Save Draft
            </button>
          </div>
        </div>

        {/* Proposal modal */}
        {!schemeId && (
          <div className="fixed inset-0 left-20 bg-slate-100 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-lg border border-gray-200">
              <div className="mb-6 flex items-center gap-2 text-blue-700 bg-blue-200 border-b border-blue-300 -m-6 p-3 rounded-t-xl font-bold text-lg">
                <NewReleasesSharp /> Add New Proposal
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proposal Name
              </label>

              <input
                type="text"
                value={proposalName}
                onChange={(e) => setProposalName(e.target.value)}
                placeholder="Enter proposal name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />

              <div className="justify-end mt-10 text-right flex gap-4">
                <button
                  className="px-4 py-2 disabled:cursor-not-allowed bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Go Back
                </button>

                <button
                  className="px-4 py-2 disabled:cursor-not-allowed bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  onClick={handleCreateScheme}
                  disabled={loading}
                >
                  {`${loading ? "Submitting" : "Submit"}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {draftLoading && <LoadingComponent />}
      </div>
    </FormThemeContext.Provider>
  );
};

export default NewScheme;
